import {Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {AsyncPipe, DecimalPipe, NgForOf, NgIf} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {Pagination} from "./pagination.model";

@Component({
  selector: 'app-advanced-search-pagination',
  standalone: true,
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    TranslateModule,
    AsyncPipe,
    DecimalPipe
  ],
  templateUrl: './advanced-search-pagination.component.html',
  styleUrl: './advanced-search-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedSearchPaginationComponent implements OnChanges {

  @Input() pagination: Pagination = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  };
  @Output() pageChanged = new EventEmitter<number>();
  @Output() perPageChanged = new EventEmitter<Pagination>();

  pages: (number | string)[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pagination']) {
      // Ensure pagination values are valid numbers
      this.pagination = {
        current_page: Math.max(1, Number(this.pagination?.current_page) || 1),
        last_page: Math.max(1, Number(this.pagination?.last_page) || 1),
        per_page: Math.max(1, Number(this.pagination?.per_page) || 10),
        total: Math.max(0, Number(this.pagination?.total) || 0)
      };

      this.calculatePages();

      // Force change detection
      this.cdr.markForCheck();
    }
  }

  private calculatePages(): void {
    const total = Math.max(1, this.pagination.last_page);
    const current = Math.min(Math.max(1, this.pagination.current_page), total);
    const newPages: (number | string)[] = [];

    if (total <= 7) {
      // If total pages is 7 or less, show all pages
      for (let i = 1; i <= total; i++) {
        newPages.push(i);
      }
    } else {
      // Always show first page
      newPages.push(1);

      if (current <= 4) {
        // If current page is near the start
        for (let i = 2; i <= 5; i++) {
          newPages.push(i);
        }
        if (total > 6) {
          newPages.push('...');
          newPages.push(total);
        }
      } else if (current >= total - 3) {
        // If current page is near the end
        newPages.push('...');
        for (let i = total - 4; i <= total; i++) {
          newPages.push(i);
        }
      } else {
        // If current page is in the middle
        newPages.push('...');
        for (let i = current - 1; i <= current + 1; i++) {
          newPages.push(i);
        }
        newPages.push('...');
        newPages.push(total);
      }
    }

    this.pages = newPages;
  }

  onSelectPage(page: number | string): void {
    if (typeof page === 'number' &&
      page !== this.pagination.current_page &&
      page >= 1 &&
      page <= this.pagination.last_page) {
      this.pageChanged.emit(page);
    }
  }

  onPerPageChange(value: number): void {
    const updatedPagination: Pagination = {
      ...this.pagination,
      per_page: value,
      current_page: 1
    };
    this.perPageChanged.emit(updatedPagination);
  }
}
